'use client';

import { useEffect, useState } from 'react';
import { blobRefHash, getBlobUrl, isBlobRef, onBlobArrival } from './blobStore';

/**
 * Resolve a photo reference string to a usable `<img src>`.
 *
 *   - `undefined` or empty → returns `undefined` (caller should hide the img)
 *   - `data:image/…` legacy URLs → passed through synchronously
 *   - `http(s)://…` → passed through synchronously
 *   - `blob:sha256-<hex>` → resolved asynchronously via the blob store,
 *     returns `undefined` until the ObjectURL is ready
 *
 * Subscribes to `onBlobArrival` so that blobs which land AFTER the
 * first render (the common case post-pair-sync: Dexie row arrives via
 * update → <img> mounts → requestMissingBlobs → chunks stream in a
 * few seconds later) re-resolve without needing the user to reload
 * the page. Without this the image tile stays blank until a manual
 * refresh — Veronika's 2026-04-23 report.
 */
export function useBlobUrl(photo: string | undefined | null): string | undefined {
  const initial = photo && !isBlobRef(photo) ? photo : undefined;
  const [url, setUrl] = useState<string | undefined>(initial);

  useEffect(() => {
    if (!photo) { setUrl(undefined); return; }
    if (!isBlobRef(photo)) { setUrl(photo); return; }
    const targetHash = blobRefHash(photo);
    let cancelled = false;

    const resolve = () => {
      getBlobUrl(targetHash).then(u => {
        if (!cancelled) setUrl(u || undefined);
      });
    };
    resolve();

    const unsubscribe = onBlobArrival((hash) => {
      if (hash === targetHash) resolve();
    });
    return () => { cancelled = true; unsubscribe(); };
  }, [photo]);

  return url;
}
